// Vendored Mesh Spheres engine (Stripe / kevinhufnagl mesh). Shared WebGL
// context per palette; blit to 2D canvases.

import { Gradient } from './stripe.gradient.js';

const engines = new Map();
const animators = new Set();
let raf = null;
let lastFrame = 0;
const FPS = 40;

async function getEngine(colorsKey) {
  let entry = engines.get(colorsKey);
  if (entry) return entry.ready;

  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 600;
  canvas.style.position = 'fixed';
  canvas.style.left = '-10000px';
  canvas.style.top = '0';
  canvas.style.width = '1024px';
  canvas.style.height = '600px';
  document.body.appendChild(canvas);

  const gradient = new Gradient();
  gradient.el = canvas;
  gradient.inputColors = colorsKey.split(',');

  const ready = (async () => {
    await gradient.connect();
    const u = gradient.mesh.material.uniforms;
    return {
      canvas, gradient,
      base: {
        amp: gradient.amp,
        freq: [gradient.freqX, gradient.freqY],
      },
      uniforms: {
        amp: u.u_vertDeform.value.noiseAmp,
        freq: u.u_global.value.noiseFreq,
        time: u.u_time,
      },
    };
  })();

  engines.set(colorsKey, { ready });
  return ready;
}

function renderFrame(eng, cfg) {
  eng.uniforms.time.value = 1253106 + cfg.meshId * 1000;
  eng.uniforms.amp.value = eng.base.amp * (1 + cfg.ampMod);
  eng.uniforms.freq.value = [
    eng.base.freq[0] * (1 + cfg.freqMod[0]),
    eng.base.freq[1] * (1 + cfg.freqMod[1]),
  ];
  eng.gradient.minigl.render();
}

function blit(src, dst, panX, rot) {
  const rect = dst.getBoundingClientRect();
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const w = Math.max(1, Math.round(rect.width * dpr));
  const h = Math.max(1, Math.round(rect.height * dpr));
  if (dst.width !== w || dst.height !== h) { dst.width = w; dst.height = h; }
  const ctx = dst.getContext('2d');
  const aspect = w / h;
  let sw = src.height * aspect, sh = src.height, sx = 0, sy = 0;
  if (sw > src.width) { sw = src.width; sh = src.width / aspect; sy = (src.height - sh) / 2; }
  const slack = Math.max(0, src.width - sw);
  sx = Math.max(0, Math.min(slack, slack * (0.5 + (panX || 0) * 0.5)));
  ctx.clearRect(0, 0, w, h);
  const c = Math.abs(Math.cos(rot || 0)), s = Math.abs(Math.sin(rot || 0));
  const need = Math.max(c + aspect * s, s + c / aspect);
  const COVER = Math.max(1.42, need * 1.02);
  ctx.save();
  ctx.translate(w / 2, h / 2);
  if (rot) ctx.rotate(rot);
  ctx.scale(COVER, COVER);
  ctx.drawImage(src, sx, sy, sw, sh, -w / 2, -h / 2, w, h);
  ctx.restore();
}

export async function paint(target, cfg) {
  const eng = await getEngine(cfg.colors);
  renderFrame(eng, cfg);
  blit(eng.canvas, target, cfg.panX, cfg.rot);
}

function tick(now) {
  raf = null;
  if (!animators.size) return;
  if (now - lastFrame >= 1000 / FPS) {
    lastFrame = now;
    const byKey = new Map();
    for (const a of animators) {
      const cfg = a();
      if (!cfg) continue;
      const key = cfg.colors + '|' + cfg.meshId + '|' + cfg.ampMod.toFixed(4) + '|' + cfg.freqMod.join(',');
      if (!byKey.has(key)) byKey.set(key, { cfg, targets: [] });
      byKey.get(key).targets.push({ el: cfg.target, panX: cfg.panX, rot: cfg.rot });
    }
    for (const { cfg, targets } of byKey.values()) {
      const entry = engines.get(cfg.colors);
      if (!entry || !entry.resolved) continue;
      renderFrame(entry.resolved, cfg);
      for (const t of targets) blit(entry.resolved.canvas, t.el, t.panX, t.rot);
    }
  }
  raf = requestAnimationFrame(tick);
}

export async function register(fn, colors) {
  const eng = await getEngine(colors);
  const entry = engines.get(colors);
  entry.resolved = eng;
  animators.add(fn);
  if (!raf) raf = requestAnimationFrame(tick);
}

export function unregister(fn) {
  animators.delete(fn);
  if (!animators.size && raf) { cancelAnimationFrame(raf); raf = null; }
}

export async function warm(colors) {
  const eng = await getEngine(colors);
  engines.get(colors).resolved = eng;
  return eng;
}
